import { AIType } from "@evidenceb/ai-handler";
import { WorkModeType } from "@evidenceb/athena-common/interfaces/Workmode";
import type { HierarchyIds, HistoryItem } from "@evidenceb/gameplay-interfaces";
import type { Activity, Statement } from "@xapi/xapi";
import uniqBy from "lodash/uniqBy";
import {
  ADAPTIVE_TEST_CATEGORY,
  AI_CATEGORIES,
  VERBS,
  WORKMODES_CATEGORIES,
  XAPI_REGISTRY,
} from "./const";
import type { WorkmodesWithStatements } from "./types";

type StatementsFilter = (
  value: Statement,
  index: number,
  array: Statement[],
) => boolean;

type StatementsMap<T> = (
  value: Statement,
  index: number,
  array: Statement[],
) => T;

type StatementsSort = (a: Statement, b: Statement) => number;

type StatementsFind = (
  value: Statement,
  index: number,
  array: Statement[],
) => Statement | undefined;

/**
 * @param id id of the module
 * @return a filter function that filters statements from the modile
 */
export const byModuleStatementsFilter = (id: string): StatementsFilter => {
  return (value: Statement) => {
    return isStatementInModule(value, id);
  };
};

export const byWorkmodeStatementsFilter = (
  workmode: WorkmodesWithStatements,
): StatementsFilter => {
  return (statement) => {
    return isWorkmodeStatement(workmode)(statement);
  };
};
export const isWorkmodeStatement =
  (workmode: WorkmodesWithStatements) =>
  (statement: Statement): boolean => {
    return (
      statement.context?.contextActivities?.category?.some(
        (category) => category.id === WORKMODES_CATEGORIES[workmode],
      ) ?? false
    );
  };

export const isStatementInModule = (
  statement: Statement,
  id: string | undefined,
) => {
  let moduleId: string | undefined;
  try {
    const { moduleId: _moduleId } = contextToHierarchyId(statement.context!);
    moduleId = _moduleId;
  } catch {
    // ignore
  }
  if (!moduleId) {
    try {
      moduleId = objectToModuleId(statement.object! as Activity);
    } catch {
      return false;
    }
  }
  return moduleId === id;
};

export const byAITypeStatementsFilter = (aiType: AIType): StatementsFilter => {
  return (statement) => {
    return isAITypeStatement(aiType)(statement);
  };
};
export const isAITypeStatement =
  (aiType: AIType) =>
  (statement: Statement): boolean => {
    return (
      statement.context?.contextActivities?.category?.some(
        (category) => category.id === AI_CATEGORIES[aiType],
      ) ?? false
    );
  };
export const zpdesStatementsFilter: StatementsFilter = (statement) => {
  if (isAITypeStatement(AIType.BanditManchot)(statement)) return true;
  // Also return true if no ai is set (old statements)
  if (
    Object.values(AIType).every((type) => !isAITypeStatement(type)(statement))
  )
    return true;
  return false;
};

export const isDiagStatement = (statement: Statement) => {
  return (
    statement.verb.id === VERBS.completed.id &&
    (statement.context?.contextActivities?.category?.some(
      (category) => category.id === ADAPTIVE_TEST_CATEGORY,
    ) ??
      false)
  );
};

type CommonExtension = { isInitialTest: boolean; isAdaptiveTest: boolean };

export const isInitialTestStatement = (statement: Statement): boolean => {
  return (
    (
      statement.context?.extensions?.[`${XAPI_REGISTRY}/extensions/common`] as
        | CommonExtension
        | undefined
    )?.isInitialTest ?? false
  );
};

export const isExerciseStatement = (statement: Statement) => {
  if (
    "objectType" in statement.object &&
    statement.object.objectType === "Activity"
  ) {
    return statement.object.id.startsWith(`${XAPI_REGISTRY}/exercise`);
  }
  return false;
};

/**
 * Filter function to filter statements that are part of the solo AI and NOT adaptive test
 * @param value
 * @param index
 * @param array
 * @return boolean
 */
export const soloAIStatementsFilter: StatementsFilter = (value) => {
  if (value.verb.id !== VERBS.failed.id && value.verb.id !== VERBS.passed.id)
    return false;
  if (
    value.context?.contextActivities?.category?.some(
      (category) => category.id === ADAPTIVE_TEST_CATEGORY,
    )
  )
    return false;
  if (
    value.context?.contextActivities?.category?.some(
      ({ id }) =>
        id !== WORKMODES_CATEGORIES[WorkModeType.SoloAI] &&
        Object.values(WORKMODES_CATEGORIES).includes(id),
    ) // has either no workmode (old statements) or the soloAI workmode
  )
    return false;
  return true;
};

/**
 * The exercises the student answered in a module's solo AI course, oldest
 * first
 */
export const moduleSoloAIExerciseStatements = (
  statements: Statement[],
  moduleId: string,
): Statement[] =>
  statements
    .filter(soloAIStatementsFilter)
    .filter(zpdesStatementsFilter)
    .filter(byModuleStatementsFilter(moduleId))
    .sort(chronologicalStatementsSort);

/**
 * Filter function to filter statements that are part of the solo AI and NOT adaptive test
 * @param value
 * @param index
 * @param array
 * @return boolean
 */
export const soloAIAOrATStatementsFilter: StatementsFilter = (value) => {
  if (value.verb.id !== VERBS.failed.id && value.verb.id !== VERBS.passed.id)
    return false;
  if (
    value.context?.contextActivities?.category?.some(
      ({ id }) =>
        id !== WORKMODES_CATEGORIES[WorkModeType.SoloAI] &&
        Object.values(WORKMODES_CATEGORIES).includes(id),
    ) // has either no workmode (old statements) or the soloAI workmode
  )
    return false;
  return true;
};

/**
 * @param value
 * @returns an history item with only score and exerciseId
 */
export const toHistoryStatementsMap: StatementsMap<HistoryItem> = (value) => {
  const unixTimestamp = new Date(value.timestamp || Date.now())
    .valueOf()
    .toString();
  return {
    exerciseId: objectToExerciseId(value.object as Activity),
    score: value.result!.score!.raw!,
    timestamp: unixTimestamp,
  };
};

/**
 * Sort function to sort the statements in chronological order
 */
export const chronologicalStatementsSort: StatementsSort = (a, b) => {
  if (!a.timestamp) return -1;
  if (!b.timestamp) return 1;
  return Date.parse(a.timestamp) - Date.parse(b.timestamp);
};

/** Sorts timestamps from most to least recent */
export const reverseChronologicalStatementsSort: StatementsSort = (a, b) => {
  if (!a.timestamp) return -1;
  if (!b.timestamp) return 1;
  return Date.parse(b.timestamp) - Date.parse(a.timestamp);
};

/**
 * Deduplicate an array of statements, assuming that two statements with the
 * same id are identical.
 */
export const deduplicateStatements = (statements: Statement[]): Statement[] => {
  return uniqBy(statements, "id");
};

/**
 *
 */
export const adaptiveTestStatementsFilter: StatementsFilter = (value) => {
  if (value.verb.id !== VERBS.failed.id && value.verb.id !== VERBS.passed.id)
    return false;
  if (
    !value.context?.contextActivities?.category ||
    value.context.contextActivities.category.every(
      (category) => category.id !== ADAPTIVE_TEST_CATEGORY,
    )
  )
    return false;
  return true;
};

/**
 * @param number reference number of the targeted adaptive test
 * @returns a filter function that filters the adaptive test statements
 * pertaining to a specific test
 */
export const byAdaptiveTestNumberStatementsFilter = (
  number: number,
): StatementsFilter => {
  return (value: Statement) => isStatementFromTest(value, number);
};

export const isStatementFromTest = (
  statement: Statement,
  testNumber: Number,
) => {
  if (!statement.context?.extensions) return false;
  return (
    statement.context?.extensions[
      `${XAPI_REGISTRY}/extensions/adaptive-test`
    ] === testNumber
  );
};

export const adaptiveTestDiagsStatementsFilter: StatementsFilter = (
  statement,
) => {
  return isStatementDiag(statement);
};

export const isStatementDiag = (statement: Statement) => {
  return (
    statement.verb.id === VERBS.completed.id &&
    (statement.context?.contextActivities?.category?.some(
      (category) => category.id === ADAPTIVE_TEST_CATEGORY,
    ) ??
      false)
  );
};

export const activityVideoTutorialWatchedStatementsFilter: StatementsFilter = (
  statement,
) => {
  return (
    statement.verb.id === VERBS.viewed.id &&
    "objectType" in statement.object &&
    statement.object.objectType === "Activity" &&
    (statement.object as Activity).definition?.type ===
      `${XAPI_REGISTRY}/activities/activity-video-tutorial`
  );
};

export const byAdaptiveTestNumberStatementsFind =
  (testNumber: number): StatementsFind =>
  (statement) => {
    if (!statement.context?.extensions) return undefined;
    return statement.context?.extensions[
      `${XAPI_REGISTRY}/extensions/adaptive-test`
    ] === testNumber
      ? statement
      : undefined;
  };

/*****************************************************************************/

/**
 * @param statements list of chronologically ordered statements
 * @returns the number of the last adaptive test statement or undefined if there is no statement
 */
export const getLastAdaptiveTestNumber = (
  statements: Statement[],
): number | undefined => {
  return Number(
    statements[statements.length - 1]?.context!.extensions![
      `${XAPI_REGISTRY}/extensions/adaptive-test`
    ],
  );
};

export const byEntryLearningSetIdFilter =
  (entryLearningSet: string): StatementsFilter =>
  (statement) =>
    statement.context?.contextActivities?.parent?.some(
      (parent) =>
        (parent as Activity).id.startsWith(`${XAPI_REGISTRY}/learning-set/`) &&
        (parent as Activity).id.split("/").reverse()[0] === entryLearningSet,
    ) ?? false;

/*****************************************************************************/

export const contextToHierarchyId = (
  context: Required<Statement>["context"],
): Omit<HierarchyIds, "exerciseId" | "isInitialTest"> => {
  const re = new RegExp(
    `${XAPI_REGISTRY}/module/(.*?)/objective/(.*?)/activity/(.*)`,
    "",
  );
  const result = re[Symbol.match](context.contextActivities!.parent![0].id);
  return {
    moduleId: result![1],
    objectiveId: result![2],
    activityId: result![3],
  };
};

const objectToExerciseId = (object: Activity): string => {
  const re = new RegExp(`${XAPI_REGISTRY}/exercise/(.*)`);
  const result = re[Symbol.match](object.id);
  return result![1];
};

const objectToModuleId = (object: Activity): string => {
  const re = new RegExp(`${XAPI_REGISTRY}/module/(.*)`);
  const result = re[Symbol.match](object.id);
  return result![1];
};
